import { enableProdMode, Injector } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { TokenstorageService } from '../src/app/core/services/token-storage/tokenstorage.service';
// Corrects PrimeNG's buggy sidebar ESC guard so ESC closes only the top-most
// (nested) sidebar instead of all open ones. Must load before bootstrap.
import './patches/primeng-sidebar-esc.patch';
// Lets body-appended overlays (every paginator rows-per-page dropdown) open
// upward when there is no room below — PrimeNG measures the panel as 0px tall
// and so never flips. Must load before bootstrap.
import './patches/primeng-overlay-flip.patch';
// Captures the browser's `beforeinstallprompt` offer. It fires once and can arrive
// before the first component exists, so the listener has to be attached ahead of
// bootstrap or the "Add to home screen" prompt can never be shown.
import './app/core/services/pwa/install-prompt';

// IMMEDIATE CSP FIX - Override setAttribute and createElement before any other code runs
(function() {
  // Override setAttribute to prevent inline style CSP violations
  const originalSetAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function(name: string, value: string) {
    if (name === 'style' && this instanceof HTMLElement) {
      // Try using style property directly instead of inline attribute
      try {
        if (value && typeof value === 'string' && value.includes(':')) {
          const rules = value.split(';').filter(rule => rule.trim());
          rules.forEach(rule => {
            const [property, val] = rule.split(':').map(s => s.trim());
            if (property && val) {
              // Convert kebab-case to camelCase for style properties
              const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
              try {
                (this as any).style[camelProperty] = val;
              } catch (e) {
                // Fallback to direct property access
                (this as any).style.setProperty(property, val);
              }
            }
          });
          return; // Don't call original setAttribute
        }
      } catch (e) {
        console.warn('Failed to apply CSP-compliant styles:', e);
        return; // Skip style application rather than violate CSP
      }
      return; // Don't call original setAttribute for style
    }
    return originalSetAttribute.call(this, name, value);
  };

  // Override createElement to add nonce to style and script elements
  const originalCreateElement = document.createElement;
  document.createElement = function(tagName: string) {
    const element = originalCreateElement.call(document, tagName);
    const tag = tagName.toLowerCase();
    if (tag === 'style') {
      element.setAttribute('nonce', 'automatrdynamicstyle');
    } else if (tag === 'script') {
      element.setAttribute('nonce', 'automatrdynamicscripts');
    }
    return element;
  };

  // Override appendChild to add nonce to style elements
  const originalAppendChild = Node.prototype.appendChild;
  Node.prototype.appendChild = function<T extends Node>(newChild: T): T {
    if (newChild instanceof HTMLStyleElement && !newChild.hasAttribute('nonce')) {
      newChild.setAttribute('nonce', 'automatrdynamicstyle');
    } else if (newChild instanceof HTMLScriptElement && !newChild.hasAttribute('nonce')) {
      newChild.setAttribute('nonce', 'automatrdynamicscripts');
    }
    return originalAppendChild.call(this, newChild);
  };
})();

// Configure Formio to not use CDN
declare const Formio: any;
if (typeof Formio !== 'undefined') {
  Formio.config = {
    ...Formio.config,
    cdn: null,  // Disable CDN usage
    base: window.location.origin,  // Use local server as base
  };
}

let tokenService: TokenstorageService;

function initializeApp(injector: Injector) {
  tokenService = injector.get(TokenstorageService);
}

if (environment.production) {
  enableProdMode();
}

const originalFetch = window.fetch;
window.fetch = async function (input, init) {
  const apiUrl:any = input;

  init = init || {};
  init.headers = init.headers || {};

  // Get the token
  const token = tokenService ? tokenService.getToken() : '';

  const data = window.location.pathname.includes('/formbuilder');

  if (apiUrl.startsWith(environment.serviceurl)) {
    if (data) {
      if (token) {
        const headers = new Headers(init.headers);
        if (!headers.has('Authorization') && token){

          init.headers = {
            ...init.headers,
            'Authorization': `Bearer ${token}`
          };
        }
      }
    }
  }

  return originalFetch(input, init);
};

(function () {
  const originalXMLHttpRequest = window.XMLHttpRequest;
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
  XMLHttpRequest.prototype.open = function () {
    this._method = arguments[0];
    this._url = arguments[1];

    this._headers = this._headers || {};
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
    // Store the headers in the _headers object
    this._headers[name] = value;
    return originalSetRequestHeader.call(this, name, value);
  };


  XMLHttpRequest.prototype.send = function () {
    if (this._url.startsWith(environment.serviceurl)) {
      const token = tokenService ? tokenService.getToken() : '';
      if (!this._headers) {
        this._headers = {};
      }
      const data = window.location.pathname.includes('/formbuilder');
      if (data) {
        if (token) {
          if(!this._headers['Authorization']){
            this.setRequestHeader('Authorization', `Bearer ${token}`);
          }
        }
      }
    }
    return originalSend.apply(this, arguments);
  };
})();

platformBrowserDynamic().bootstrapModule(AppModule)
  .then(moduleRef => {
    const injector = moduleRef.injector;
    initializeApp(injector);  // Initialize the token service
  })
  .catch(err => console.error(err));


// CSP handling is now done by csp-polyfill.ts loaded in polyfills.ts
// This ensures there's no conflict between different CSP implementations